

import { useState, useEffect, useMemo } from "react";
import { 
  Search, 
  Receipt,
  Download,
  FileText,
  Tag,
} from "lucide-react";
// @ts-ignore - react-csv doesn't have types
import { CSVLink } from "react-csv";

import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import SearchableDropdown from "../../components/SearchableDropdown";
import { expensesApi, vendorsApi, eventsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { ReportTable } from "../../components/ReportTable";

// Types
interface Expense {
  id: number;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_category_id?: number;
  category?: { id: number; name: string };
  vendor_id?: number;
  vendor?: { id: number; name: string };
  event_id?: number;
  event?: { id: number; name: string; start_date?: string; client_name?: string };
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  proof_path?: string;
  status: string;
  created_at?: string;
}

export default function ExpensesReportPage() {
  const { showToast } = useToast();
  
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [vendors, setVendors] = useState<{ id: number; name: string }[]>([]);
  const [events, setEvents] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    loadData();
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      // Load payment methods
      const pmRes = await mastersApi.paymentMethods.list({ status: 'active' });
      const pmData = pmRes.data?.data || pmRes.data || [];
      if (Array.isArray(pmData)) {
        setPaymentMethods(pmData.map((m: any) => m.name));
      }
    } catch (error) {
      console.error("Failed to load filters:", error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesRes, categoriesRes, vendorsRes, eventsRes] = await Promise.all([
        expensesApi.list(),
        mastersApi.expenseCategories.list(),
        vendorsApi.list(),
        eventsApi.list(),
      ]);
      
      // Handle different response formats
      const expensesData = expensesRes.data?.data || expensesRes.data || [];
      setExpenses(Array.isArray(expensesData) ? expensesData : []);
      
      const categoriesData = categoriesRes.data?.data || categoriesRes.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      
      const vendorsData = vendorsRes.data?.data || vendorsRes.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      
      const eventsData = eventsRes.data?.data || eventsRes.data || [];
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error: any) {
      console.error("Error loading expenses:", error);
      showToast(error?.message || "Failed to load expenses data", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = useMemo(() => {
    let filtered = Array.isArray(expenses) ? expenses : [];
    
    // Date range filter
    if (fromDate && toDate) {
      filtered = filtered.filter(e => {
        const expenseDate = new Date(e.expense_date);
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return expenseDate >= from && expenseDate <= to;
      });
    }
    
    // Search query filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        (e.title || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.category?.name || '').toLowerCase().includes(q) ||
        (e.vendor?.name || '').toLowerCase().includes(q) ||
        (e.event?.name || '').toLowerCase().includes(q) ||
        (e.reference_number || '').toLowerCase().includes(q)
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(e => String(e.expense_category_id) === categoryFilter);
    }
    
    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(e => String(e.vendor_id) === vendorFilter);
    }
    
    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(e => String(e.event_id) === eventFilter);
    }
    
    // Payment method filter
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(e => e.payment_method === paymentMethodFilter);
    }
    
    return filtered;
  }, [expenses, searchQuery, categoryFilter, vendorFilter, eventFilter, paymentMethodFilter, fromDate, toDate]);

  const csvData = useMemo(() => {
    return filteredExpenses.map(expense => ({
      'Expense ID': expense.id,
      'Title': expense.title,
      'Description': expense.description || '',
      'Category': expense.category?.name || '',
      'Amount': expense.amount,
      'Expense Date': formatDate(expense.expense_date),
      'Payment Method': expense.payment_method || '',
      'Reference': expense.reference_number || '',
      'Vendor': expense.vendor?.name || '',
      'Event': expense.event?.name || '',
      'Event Date': expense.event?.start_date ? formatDate(expense.event.start_date) : '',
      'Notes': expense.notes || '',
    }));
  }, [filteredExpenses]);

  const csvHeaders = [
    { label: 'Expense ID', key: 'Expense ID' },
    { label: 'Title', key: 'Title' },
    { label: 'Description', key: 'Description' },
    { label: 'Category', key: 'Category' },
    { label: 'Amount', key: 'Amount' },
    { label: 'Expense Date', key: 'Expense Date' },
    { label: 'Payment Method', key: 'Payment Method' },
    { label: 'Reference', key: 'Reference' },
    { label: 'Vendor', key: 'Vendor' },
    { label: 'Event', key: 'Event' },
    { label: 'Event Date', key: 'Event Date' },
    { label: 'Notes', key: 'Notes' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Expenses Report</h1>
        <p className="text-sm text-stone-500">View and analyze expense records</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search expenses..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Categories" },
              ...categories.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
              })),
            ]}
            value={categoryFilter}
            onChange={setCategoryFilter}
            placeholder="Filter by category"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Vendors" },
              ...vendors.map((vendor) => ({
                value: String(vendor.id),
                label: vendor.name,
              })),
            ]}
            value={vendorFilter}
            onChange={setVendorFilter}
            placeholder="Filter by vendor"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Events" },
              ...events.map((event) => ({
                value: String(event.id),
                label: event.name,
              })),
            ]}
            value={eventFilter}
            onChange={setEventFilter}
            placeholder="Filter by event"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Methods" },
              ...paymentMethods.map((pm) => ({
                value: pm,
                label: pm,
              })),
            ]}
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
            placeholder="Payment method"
          />
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`expenses-report-${new Date().toISOString().split('T')[0]}.csv`}
            className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Content */}
      {filteredExpenses.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No expenses found</p>
        </div>
      ) : (
        <ReportTable
          columns={[
            { key: 'title', label: 'Title', width: '25%' },
            { key: 'category', label: 'Category', width: '15%' },
            { key: 'date', label: 'Date', width: '12%' },
            { key: 'vendor', label: 'Vendor/Event', width: '20%' },
            { key: 'method', label: 'Method', width: '13%' },
            { key: 'reference', label: 'Reference', width: '15%' },
            { key: 'amount', label: 'Amount', align: 'right', width: '10%' },
          ]}
          data={filteredExpenses.map(expense => ({
            id: expense.id,
            title: (
              <div>
                <span className="font-medium text-stone-800">{expense.title}</span>
                {expense.description && (
                  <p className="text-xs text-stone-400 truncate max-w-[250px]">{expense.description}</p>
                )}
                {expense.reference_number && (
                  <p className="text-xs text-stone-400">Ref: {expense.reference_number}</p>
                )}
              </div>
            ),
            category: (
              <div className="flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-stone-400" />
                <span>{expense.category?.name || '-'}</span>
              </div>
            ),
            date: formatDate(expense.expense_date),
            vendor: expense.vendor?.name || expense.event?.name || '-',
            method: <span className="capitalize">{expense.payment_method || '-'}</span>,
            reference: expense.reference_number || '-',
            amount: expense.amount,
          }))}
          totalField="amount"
          formatValue={(value, key) => {
            if (key === 'amount') {
              return <span className="font-bold text-amber-600">{formatCurrencyFull(value)}</span>;
            }
            return value;
          }}
        />
      )}
    </DashboardLayout>
  );
}
